import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Leaf, Heart, Mail, Github, Twitter, ExternalLink, Sparkles, Settings, Gift, Copy, Share2, Check, X, Instagram, Linkedin, Facebook, Youtube } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

export const Footer = () => {
  const { t, i18n } = useTranslation();
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [referralStats, setReferralStats] = useState({ total_referrals: 0, free_months_earned: 0 });
  const [copied, setCopied] = useState(false);
  const [footerConfig, setFooterConfig] = useState(null);

  // Helper to get text based on language
  const getText = (textObj) => {
    if (!textObj) return '';
    if (typeof textObj === 'string') return textObj;
    const currentLang = i18n.language?.startsWith('de') ? 'de' : 'en';
    return textObj[currentLang] || textObj.de || textObj.en || '';
  };

  // Get social icon component
  const getSocialIcon = (platform) => {
    const icons = {
      twitter: Twitter,
      instagram: Instagram,
      facebook: Facebook,
      linkedin: Linkedin,
      youtube: Youtube,
      github: Github
    };
    return icons[platform] || ExternalLink;
  };
  
  // Fetch footer config
  useEffect(() => {
    const fetchFooterConfig = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/homepage-config`);
        if (response.ok) {
          const data = await response.json();
          setFooterConfig(data.footer_config);
        }
      } catch (error) {
        console.error('Error fetching footer config:', error);
      }
    };
    fetchFooterConfig();
  }, []);

  // Check if user is superadmin and logged in
  useEffect(() => {
    const checkUser = () => {
      try {
        const userStr = localStorage.getItem('user');
        const token = localStorage.getItem('token');
        
        if (userStr && token) {
          const user = JSON.parse(userStr);
          setIsSuperAdmin(user.role === 'superadmin');
          setIsLoggedIn(true);
        } else {
          setIsSuperAdmin(false);
          setIsLoggedIn(false);
        }
      } catch (e) {
        setIsSuperAdmin(false);
        setIsLoggedIn(false);
      }
    };
    
    checkUser();
    const interval = setInterval(checkUser, 2000);
    window.addEventListener('storage', checkUser);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', checkUser);
    };
  }, []);

  // Fetch referral code when modal opens
  const fetchReferralCode = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error(i18n.language === 'de' ? 'Bitte zuerst anmelden' : 'Please login first');
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/payments/subscription`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.ok) {
        const data = await response.json();
        setReferralCode(data.referral_code || '');
        setReferralStats(data.referral_stats || { total_referrals: 0, free_months_earned: 0 });
        setShowReferralModal(true);
      }
    } catch (error) {
      console.error('Error fetching referral code:', error);
      toast.error(i18n.language === 'de' ? 'Fehler beim Laden' : 'Error loading');
    }
  };

  const copyReferralLink = () => {
    const link = `${window.location.origin}/subscription?ref=${referralCode}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success(i18n.language === 'de' ? 'Link kopiert!' : 'Link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const shareReferral = async () => {
    const link = `${window.location.origin}/subscription?ref=${referralCode}`;
    const text = i18n.language === 'de' 
      ? 'Entdecke AsceticLife - Deine App für achtsames Leben! 🧘‍♀️' 
      : 'Discover AsceticLife - Your app for mindful living! 🧘‍♀️';
    
    if (navigator.share) {
      try {
        await navigator.share({ title: 'AsceticLife', text, url: link });
      } catch (e) {
        copyReferralLink();
      }
    } else {
      copyReferralLink();
    }
  };
  
  // Default footer links (fallback if config not loaded)
  const defaultFooterLinks = [
    {
      title: { de: 'Funktionen', en: 'Features' },
      links: [
        { path: '/features', label: { de: 'Free vs. Premium', en: 'Free vs. Premium' } },
        { path: '/practices', label: { de: t('nav.practices'), en: 'Practices' } },
        { path: '/journal', label: { de: t('nav.journal'), en: 'Journal' } },
        { path: '/progress', label: { de: t('nav.progress'), en: 'Progress' } },
      ]
    },
    {
      title: { de: 'Mehr', en: 'More' },
      links: [
        { path: '/calendar', label: { de: t('nav.calendar'), en: 'Calendar' } },
        { path: '/projects', label: { de: t('nav.projects'), en: 'Projects' } },
        { path: '/learning', label: { de: t('nav.learning'), en: 'Click2Learn' } },
        { path: '/chatbot', label: { de: 'Alltags-Coach', en: 'Daily Coach' } },
      ]
    },
    {
      title: { de: 'Rechtliches', en: 'Legal' },
      links: [
        { path: '/privacy', label: { de: 'Datenschutz', en: 'Privacy Policy' } },
        { path: '/terms', label: { de: 'AGB', en: 'Terms of Service' } },
        { path: '/impressum', label: { de: 'Impressum', en: 'Legal Notice' } },
        { path: '/widerruf', label: { de: 'Widerrufsrecht', en: 'Withdrawal Policy' } },
        { path: '/widerrufsformular', label: { de: 'Widerrufsformular', en: 'Withdrawal Form' } },
      ]
    }
  ];

  // Use config or defaults - ALWAYS include legal links
  let linkSections = footerConfig?.link_sections || defaultFooterLinks;
  
  // Required legal links that must always be present
  const requiredLegalLinks = [
    { path: '/privacy', label: { de: 'Datenschutz', en: 'Privacy Policy' } },
    { path: '/terms', label: { de: 'AGB', en: 'Terms of Service' } },
    { path: '/impressum', label: { de: 'Impressum', en: 'Legal Notice' } },
    { path: '/widerruf', label: { de: 'Widerrufsrecht', en: 'Withdrawal Policy' } },
    { path: '/widerrufsformular', label: { de: 'Widerrufsformular', en: 'Withdrawal Form' } },
  ];

  // Find existing legal section or create one
  const legalSectionIndex = linkSections.findIndex(section => 
    section.links?.some(link => link.path === '/privacy' || link.path === '/terms' || link.path === '/impressum')
  );
  
  if (legalSectionIndex >= 0) {
    // Add any missing required legal links to the existing section
    const existingPaths = new Set(linkSections[legalSectionIndex].links.map(l => l.path));
    const missingLinks = requiredLegalLinks.filter(l => !existingPaths.has(l.path));
    if (missingLinks.length > 0) {
      linkSections = linkSections.map((section, i) => 
        i === legalSectionIndex 
          ? { ...section, links: [...section.links, ...missingLinks] }
          : section
      );
    }
  } else {
    linkSections = [...linkSections, {
      title: { de: 'Rechtliches', en: 'Legal' },
      links: requiredLegalLinks
    }];
  }
  const brandName = getText(footerConfig?.brand_name) || 'AsceticLife';
  const brandSubtitle = getText(footerConfig?.brand_subtitle) || 'Premium Edition';
  const tagline = getText(footerConfig?.tagline) || t('footer.tagline');
  const copyrightText = getText(footerConfig?.copyright_text) || 'A1 Alpha Consulting. Alle Rechte vorbehalten.';
  const craftedWithText = getText(footerConfig?.crafted_with_text) || (i18n.language === 'de' ? 'Erstellt mit ❤️ und Achtsamkeit' : 'Crafted with ❤️ and mindfulness');
  const showReferralBtn = footerConfig?.show_referral_button !== false;
  const referralBtnText = getText(footerConfig?.referral_button_text) || (i18n.language === 'de' ? 'Weiterempfehlen' : 'Refer a Friend');
  const socialLinks = footerConfig?.social_links || [];

  return (
    <footer className="relative mt-20 overflow-hidden">
      {/* Premium Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900 dark:from-slate-950 dark:via-black dark:to-slate-950" />
      
      {/* Animated Gradient Orbs - Green Theme */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-ascetic-600/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-emerald-600/10 rounded-full blur-3xl" />
      <div className="absolute top-1/2 right-1/3 w-64 h-64 bg-teal-600/10 rounded-full blur-3xl" />

      {/* Top Gradient Line */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-ascetic-500/50 to-transparent" />

      <div className="container mx-auto px-4 py-16 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-2 space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Link to="/" className="flex items-center gap-3 group">
                <img 
                  src="/logo.png" 
                  alt={`${brandName} Logo`}
                  className="h-12 w-12 object-contain rounded-lg"
                />
                <div>
                  <span className="text-2xl font-serif font-bold bg-gradient-to-r from-ascetic-300 via-ascetic-400 to-emerald-400 bg-clip-text text-transparent">
                    {brandName}
                  </span>
                  <span className="block text-xs font-medium text-ascetic-400/70 tracking-wider uppercase">
                    {brandSubtitle}
                  </span>
                </div>
              </Link>
              
              <p className="text-slate-400 leading-relaxed max-w-md">
                {tagline}
              </p>

              {/* Referral Button */}
              {showReferralBtn && (
                <div className="flex items-center gap-4 pt-4">
                  {isLoggedIn ? (
                    <button 
                      onClick={fetchReferralCode}
                      className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gradient-to-r from-[#006039] to-[#00915a] hover:from-[#00915a] hover:to-[#006039] text-white font-medium transition-all duration-300 shadow-lg hover:shadow-[#006039]/25"
                    >
                      <Gift className="h-5 w-5" />
                      <span>{referralBtnText}</span>
                    </button>
                  ) : (
                    <Link 
                      to="/login"
                      className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#006039]/50 text-slate-300 hover:text-white transition-all duration-300"
                    >
                      <Gift className="h-5 w-5" />
                      <span>{referralBtnText}</span>
                    </Link>
                  )}
                </div>
              )}

              {/* Social Links */}
              {socialLinks.length > 0 && (
                <div className="flex items-center gap-3 pt-4">
                  {socialLinks.map((social, index) => {
                    const SocialIcon = getSocialIcon(social.platform);
                    return (
                      <a
                        key={index}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white transition-all duration-200"
                      >
                        <SocialIcon className="h-5 w-5" />
                      </a>
                    );
                  })}
                </div>
              )}
            </motion.div>
          </div>

          {/* Dynamic Link Sections */}
          {linkSections.map((section, sectionIndex) => (
            <motion.div
              key={sectionIndex}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 * (sectionIndex + 1) }}
              className="space-y-4"
            >
              <h4 className="font-serif font-semibold text-white flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-ascetic-400" />
                {getText(section.title)}
              </h4>
              <ul className="space-y-3">
                {(section.links || []).map((link, linkIndex) => (
                  <li key={linkIndex}>
                    <Link 
                      to={link.path} 
                      className="text-slate-400 hover:text-ascetic-400 transition-colors duration-200 flex items-center gap-2 group"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-slate-600 group-hover:bg-ascetic-500 transition-colors" />
                      {getText(link.label)}
                    </Link>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Bottom Bar */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="mt-16 pt-8 border-t border-white/10"
        >
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-slate-500">
              © {new Date().getFullYear()} {copyrightText}
            </p>
            <div className="flex items-center gap-4">
              {/* Affiliate Partner Link */}
              <Link 
                to="/partner/bewerben" 
                className="text-sm text-slate-500 hover:text-[#006039] transition-colors flex items-center gap-1.5"
              >
                <Gift className="h-3.5 w-3.5" />
                Partner werden
              </Link>
              {isSuperAdmin && (
                <Link 
                  to="/admin" 
                  className="text-sm text-slate-500 hover:text-[#006039] transition-colors flex items-center gap-1.5"
                >
                  <Settings className="h-3.5 w-3.5" />
                  Admin
                </Link>
              )}
              <p className="text-sm text-slate-500 flex items-center gap-2">
                {craftedWithText.includes('❤️') ? craftedWithText : (
                  <>
                    {craftedWithText.split('❤️')[0]}
                    <Heart className="h-4 w-4 text-ascetic-400 animate-pulse" />
                    {craftedWithText.split('❤️')[1] || ''}
                  </>
                )}
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Referral Modal */}
      <AnimatePresence>
        {showReferralModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowReferralModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: "spring", duration: 0.5 }}
              className="relative w-full max-w-md bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl shadow-2xl border border-[#006039]/30 overflow-hidden"
              onClick={(e) => e.stopPropagation()}
              data-testid="referral-modal"
            >
              {/* Modal Header */}
              <div className="relative p-6 pb-4">
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#006039] via-[#00915a] to-[#006039]" />
                <button
                  onClick={() => setShowReferralModal(false)}
                  className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/10 transition-colors"
                  data-testid="referral-modal-close"
                >
                  <X className="h-5 w-5 text-slate-400" />
                </button>
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-3 rounded-xl bg-gradient-to-br from-[#006039] to-[#00915a] shadow-lg shadow-[#006039]/30">
                    <Gift className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">
                      {i18n.language === 'de' ? 'Freunde einladen' : 'Invite Friends'}
                    </h3>
                    <p className="text-sm text-slate-400">
                      {i18n.language === 'de' ? 'Teile AsceticLife & erhalte Belohnungen' : 'Share AsceticLife & get rewards'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Modal Body */}
              <div className="px-6 pb-6 space-y-5">
                {/* Reward Info */}
                <div className="p-4 rounded-xl bg-gradient-to-r from-[#006039]/20 to-emerald-900/20 border border-[#006039]/30">
                  <div className="flex items-start gap-3">
                    <Sparkles className="h-5 w-5 text-[#00915a] mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-slate-300">
                      {i18n.language === 'de' 
                        ? 'Für jeden Freund, der Premium abonniert, erhältst du einen kostenlosen Monat!' 
                        : 'For every friend who subscribes to Premium, you get one free month!'}
                    </p>
                  </div>
                </div>

                {/* Referral Code */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-300">
                    {i18n.language === 'de' ? 'Dein Empfehlungscode' : 'Your Referral Code'}
                  </label>
                  <div className="flex gap-2">
                    <div className="flex-1 px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-xl font-mono text-lg text-white tracking-wider text-center">
                      {referralCode || '...'}
                    </div>
                    <button
                      onClick={copyReferralLink}
                      className="px-4 py-3 rounded-xl bg-gradient-to-b from-[#00915a] via-[#006039] to-[#003d24] text-white shadow-lg hover:shadow-xl transition-all flex items-center gap-2"
                      data-testid="copy-referral-btn"
                    >
                      {copied ? <Check className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                {/* Share Button */}
                <button
                  onClick={shareReferral}
                  className="w-full py-3.5 rounded-xl bg-gradient-to-b from-[#00915a] via-[#006039] to-[#003d24] text-white font-medium shadow-[0_6px_20px_rgba(0,96,57,0.45)] hover:shadow-[0_8px_25px_rgba(0,96,57,0.55)] transition-all flex items-center justify-center gap-2"
                  data-testid="share-referral-btn"
                >
                  <Share2 className="h-5 w-5" />
                  {i18n.language === 'de' ? 'Link teilen' : 'Share Link'}
                </button>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-3 pt-2">
                  <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700/50 text-center">
                    <div className="text-2xl font-bold text-white">{referralStats.total_referrals}</div>
                    <div className="text-xs text-slate-400">
                      {i18n.language === 'de' ? 'Einladungen' : 'Invites'}
                    </div>
                  </div>
                  <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700/50 text-center">
                    <div className="text-2xl font-bold text-[#00915a]">{referralStats.free_months_earned}</div>
                    <div className="text-xs text-slate-400">
                      {i18n.language === 'de' ? 'Gratis-Monate' : 'Free Months'}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </footer>
  );
};
